// src/pages/Employees/index.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table, Input, Select, Space, Tag, Typography,
  Card, Row, Col, Statistic, Button, Tooltip,
  Modal, Form, Drawer, Descriptions, Empty, message, Alert, Tabs, Switch
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, UserOutlined,
  PlusOutlined, SyncOutlined, FileTextOutlined,
  RollbackOutlined, WarningOutlined, EditOutlined,
  FileOutlined, CloudServerOutlined, TeamOutlined,
  KeyOutlined, GoogleOutlined, DownloadOutlined
} from '@ant-design/icons';
import { employeesAPI, dictAPI, syncAPI, equipmentAPI, actsAPI, returnActsAPI } from '../../api/client';
import ActModal from './ActModal';
import ReturnActModal from './ReturnActModal';
import AccessTab from './AccessTab';
import LicensesTab from './LicensesTab';
import TableToolbar from '../../components/TableToolbar';
import ExportButton from '../../components/ExportButton';
import useTableSort from '../../hooks/useTableSort';
import { fetchServerTableData, makeTablePagination, normalizeTablePagination } from '../../utils/tablePagination';

const { Title, Text } = Typography;
const COMPANIES = ['Киберматч', 'ГДС', 'Inspired by sport'];

const EMPLOYEE_TYPE_COLORS = {
  employee:        'blue',
  outsource:       'orange',
  service_account: 'purple',
};
const EMPLOYEE_TYPE_LABELS = {
  employee:        'Сотрудник',
  outsource:       'Аутсорс',
  service_account: 'Сервисный',
};

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('ru-RU');
}

function getCurrentUser() {
  return {
    login:          localStorage.getItem('login'),
    is_admin:       localStorage.getItem('is_admin')       === 'true',
    is_super_admin: localStorage.getItem('is_super_admin') === 'true',
    is_security:    localStorage.getItem('is_security')    === 'true',
  };
}

export default function EmployeesPage() {
  const [search, setSearch]         = useState('');
  const [isActive, setIsActive]     = useState(true);
  const [company, setCompany]       = useState(null);
  const [empType, setEmpType]       = useState(undefined);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showAddModal, setShowAddModal]         = useState(false);
  const [showEditModal, setShowEditModal]       = useState(false);
  const [showActModal, setShowActModal]         = useState(false);
  const [showReturnModal, setShowReturnModal]   = useState(false);
  const [returnLocation, setReturnLocation]     = useState(null);
  const [syncing, setSyncing]                   = useState(false);
  const [hasAccess, setHasAccess]               = useState(undefined);
  const { sortBy, sortDir, sortIcon, getSortableHeaderProps } = useTableSort({
    initialSortBy: 'full_name',
    initialSortDir: 'asc',
    onSort: () => setPagination(p => ({ ...p, current: 1 })),
  });

  const [showPostReturnModal, setShowPostReturnModal] = useState(false);
  const [showReturnActModal, setShowReturnActModal]   = useState(false);
  const [returnActData, setReturnActData]             = useState(null);

  const [addForm]  = Form.useForm();
  const [editForm] = Form.useForm();
  const queryClient = useQueryClient();

  const currentUser = getCurrentUser();

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn:  () => dictAPI.getCompanies().then(r => r.data),
  });
  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn:  () => dictAPI.getLocations().then(r => r.data),
  });
  const { data: typeStats } = useQuery({
    queryKey: ['employees-type-stats'],
    queryFn:  () => employeesAPI.getTypeStats().then(r => r.data),
  });

  const queryParams = {
    search:        search   || undefined,
    is_active:     isActive !== undefined ? isActive : undefined,
    company:       company  || undefined,
    employee_type: empType  || undefined,
    has_access:    hasAccess !== undefined ? hasAccess : undefined,
    sort_by:       sortBy,
    sort_dir:      sortDir,
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['employees', queryParams, pagination.current, pagination.pageSize],
    queryFn:  () => fetchServerTableData(
      params => employeesAPI.getAll(params).then(r => r.data),
      queryParams,
      pagination,
    ),
  });

  const { data: employeeDetail, isLoading: detailLoading, refetch: refetchDetail } = useQuery({
    queryKey: ['employee-detail', selectedEmployee?.id],
    queryFn:  () => employeesAPI.getOne(selectedEmployee.id).then(r => r.data),
    enabled:  !!selectedEmployee?.id,
  });

  const { data: employeeActs } = useQuery({
    queryKey: ['employee-acts', selectedEmployee?.id],
    queryFn:  () => actsAPI.list(selectedEmployee.id).then(r => r.data),
    enabled:  !!selectedEmployee?.id,
  });

  const { data: employeeReturnActs } = useQuery({
    queryKey: ['employee-return-acts', selectedEmployee?.id],
    queryFn:  () => returnActsAPI.list(selectedEmployee.id).then(r => r.data),
    enabled:  !!selectedEmployee?.id,
  });

  const { data: employeeLicenses } = useQuery({
    queryKey: ['employee-licenses', selectedEmployee?.id],
    queryFn:  () => import('../../api/client').then(m =>
      m.servicesAPI.getAssignments({ employee_id: selectedEmployee.id, active_only: true, limit: 200 }).then(r => r.data)
    ),
    enabled:  !!selectedEmployee?.id,
  });

  const allActs = [
    ...(employeeActs || []).map(a => ({ ...a, _kind: 'issue' })),
    ...(employeeReturnActs || []).map(a => ({ ...a, _kind: 'return' })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const createMutation = useMutation({
    mutationFn: (data) => employeesAPI.create(data),
    onSuccess: () => {
      message.success('Сотрудник добавлен');
      setShowAddModal(false);
      addForm.resetFields();
      queryClient.invalidateQueries(['employees']);
      queryClient.invalidateQueries(['employees-type-stats']);
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => employeesAPI.update(selectedEmployee.id, data),
    onSuccess: () => {
      message.success('Данные сохранены');
      setShowEditModal(false);
      editForm.resetFields();
      queryClient.invalidateQueries(['employee-detail', selectedEmployee?.id]);
      queryClient.invalidateQueries(['employees']);
      queryClient.invalidateQueries(['employees-type-stats']);
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const returnAllMutation = useMutation({
    mutationFn: ({ equipmentIds, locationId }) => equipmentAPI.bulkAction({
      equipment_ids: equipmentIds,
      action:        'Возврат на склад',
      location_id:   locationId,
      notes:         'Снято с сотрудника',
      created_by:    localStorage.getItem('login') || 'web',
    }),
    onSuccess: (_, variables) => {
      message.success('Оборудование снято');
      const returnedEquipment = equipment.filter(e => variables.equipmentIds.includes(e.id));
      const employeeSnapshot = employeeDetail;
      setShowReturnModal(false);
      setReturnLocation(null);
      queryClient.invalidateQueries(['employee-detail', selectedEmployee?.id]);
      queryClient.invalidateQueries(['equipment']);
      refetchDetail();
      setReturnActData({ employee: employeeSnapshot, equipment: returnedEquipment });
      setShowPostReturnModal(true);
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const items     = data?.items || [];
  const total     = data?.total || 0;
  const equipment = employeeDetail?.equipment || [];
  const licensesCount = employeeLicenses?.total || 0;

  const openEdit = () => {
    if (!employeeDetail) return;
    editForm.setFieldsValue({
      full_name:        employeeDetail.full_name,
      email:            employeeDetail.email,
      job_title:        employeeDetail.job_title,
      location:         employeeDetail.location,
      joining_date:     employeeDetail.joining_date,
      notes:            employeeDetail.notes,
      employee_type:    employeeDetail.employee_type,
      is_active:        employeeDetail.is_active,
      termination_date: employeeDetail.termination_date,
    });
    setShowEditModal(true);
  };

  const handleStatClick = (type) => {
    setEmpType(prev => prev === type ? undefined : type);
    setIsActive(true);
    setPagination(p => ({ ...p, current: 1 }));
  };

  const hasFilters = Boolean(
    search || isActive !== true || company || empType || hasAccess !== undefined
  );

  const resetFilters = () => {
    setSearch('');
    setIsActive(true);
    setCompany(null);
    setEmpType(undefined);
    setHasAccess(undefined);
    setPagination(p => ({ ...p, current: 1 }));
  };

  const locationOptions = (locationsData || []).map(l => ({ label: l.name, value: l.id }));

  const openActFile = (record) => {
    if (record.drive_url) window.open(record.drive_url, '_blank');
    else if (record.drive_file_id) window.open(`https://drive.google.com/uc?export=download&id=${record.drive_file_id}`, '_blank');
    else message.error('Ссылка на акт не найдена');
  };

  const downloadActFile = (record) => {
    if (record.drive_file_id) window.open(`https://drive.google.com/uc?export=download&id=${record.drive_file_id}`, '_blank');
    else if (record.drive_url) window.open(record.drive_url, '_blank');
    else message.error('Файл акта не найден');
  };

  const actsColumns = [
    { title: 'Тип', key: 'kind', width: 80,
      render: (_, r) => r._kind === 'return'
        ? <Tag color="orange" style={{ fontSize: 10 }}>Возврат</Tag>
        : <Tag color="blue"   style={{ fontSize: 10 }}>Выдача</Tag> },
    { title: 'Номер', dataIndex: 'act_number', key: 'act_number', width: 100,
      render: (v) => <Text strong style={{ fontFamily: 'monospace' }}>{v}</Text> },
    { title: 'Дата', dataIndex: 'act_date', key: 'act_date', width: 100, render: (v) => formatDate(v) },
    { title: 'Компания', dataIndex: 'company', key: 'company', width: 120,
      render: (v) => v ? <Tag>{v}</Tag> : '—' },
    { title: 'Поз.', dataIndex: 'items_count', key: 'items_count', width: 55, align: 'center',
      render: (v) => v ?? '—' },
    { title: 'Язык', dataIndex: 'language', key: 'language', width: 60, align: 'center',
      render: (v) => v ? <Tag>{v.toUpperCase()}</Tag> : '—' },
    { title: '', key: 'dl', width: 80,
      render: (_, r) => (
        <Space>
          <Tooltip title="Открыть на Drive">
            <Button type="text" size="small" icon={<FileOutlined />}
              disabled={!r.drive_url && !r.drive_file_id} onClick={() => openActFile(r)} />
          </Tooltip>
          <Tooltip title="Скачать PDF">
            <Button type="text" size="small" icon={<DownloadOutlined />}
              disabled={!r.drive_file_id && !r.drive_url} onClick={() => downloadActFile(r)} />
          </Tooltip>
        </Space>
      )},
  ];

  const columns = [
    { title: `ФИО${sortIcon('full_name')}`, dataIndex: 'full_name', key: 'full_name', width: 220,
      onHeaderCell: () => getSortableHeaderProps('full_name'),
      render: (v, r) => (
        <Space>
          <Text strong style={{ color: r.is_active ? undefined : '#bbb' }}>{v}</Text>
          {!r.is_active && <Tag color="red" style={{ fontSize: 10 }}>Уволен</Tag>}
        </Space>
      )},
    { title: `Тип${sortIcon('employee_type')}`, dataIndex: 'employee_type', key: 'employee_type', width: 110,
      onHeaderCell: () => getSortableHeaderProps('employee_type'),
      render: (v) => v ? <Tag color={EMPLOYEE_TYPE_COLORS[v] || 'default'} style={{ fontSize: 10 }}>{EMPLOYEE_TYPE_LABELS[v] || v}</Tag> : '—' },
    { title: `Email${sortIcon('email')}`, dataIndex: 'email', key: 'email', width: 220, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('email'),
      render: (v) => v ? <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> : '—' },
    { title: `Должность${sortIcon('job_title')}`, dataIndex: 'job_title', key: 'job_title', width: 200, ellipsis: true,
      onHeaderCell: () => getSortableHeaderProps('job_title'),
      render: (v) => v ? <Tooltip title={v}>{v}</Tooltip> : '—' },
    { title: `Город${sortIcon('location')}`, dataIndex: 'location', key: 'location', width: 130,
      onHeaderCell: () => getSortableHeaderProps('location'), render: (v) => v || '—' },
    { title: `Компании${sortIcon('companies')}`, dataIndex: 'companies', key: 'companies', width: 200,
      onHeaderCell: () => getSortableHeaderProps('companies'),
      render: (v) => v ? v.split(' / ').map(c => <Tag key={c} style={{ marginBottom: 2 }}>{c}</Tag>) : <Text type="secondary">—</Text> },
    { title: 'Tribune', dataIndex: 'tribune_id', key: 'tribune_id', width: 80, align: 'center',
      render: (v) => v ? <Tag color="green" style={{ fontSize: 10 }}>Синк</Tag> : <Tag color="default" style={{ fontSize: 10 }}>Ручной</Tag> },
    { title: `Google${sortIcon('can_login')}`,
      dataIndex: 'can_login', key: 'can_login', width: 90, align: 'center',
      onHeaderCell: () => getSortableHeaderProps('can_login'),
      render: (v) => v ? <Tooltip title="Есть доступ через Google"><GoogleOutlined style={{ color: '#52c41a', fontSize: 14 }} /></Tooltip> : null },
  ];

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>Сотрудники</Title>
        <Space wrap>
          <Button icon={<SyncOutlined />} loading={syncing} onClick={async () => {
            setSyncing(true);
            try {
              const res = await syncAPI.runTribune();
              message.success(`Синк завершён: +${res.data.added} новых, ~${res.data.updated} обновлено`);
              queryClient.invalidateQueries(['employees']);
              queryClient.invalidateQueries(['employees-type-stats']);
            } catch { message.error('Ошибка синка Tribune'); }
            finally { setSyncing(false); }
          }}>Tribune</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowAddModal(true)}>Добавить</Button>
          <ExportButton
            onExport={(fileFormat) => employeesAPI.exportData(queryParams, fileFormat)}
            filenameBase="employees"
          />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Обновить</Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small"><Statistic title="Всего активных" value={typeStats?.total || 0} prefix={<UserOutlined />} /></Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small" hoverable style={{ cursor: 'pointer', borderColor: empType === 'employee' ? '#1677ff' : undefined }}
            onClick={() => handleStatClick('employee')}>
            <Statistic title="Сотрудники" value={typeStats?.employee || 0}
              prefix={<TeamOutlined style={{ color: '#1677ff' }} />}
              valueStyle={{ color: empType === 'employee' ? '#1677ff' : undefined }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small" hoverable style={{ cursor: 'pointer', borderColor: empType === 'outsource' ? 'orange' : undefined }}
            onClick={() => handleStatClick('outsource')}>
            <Statistic title="Аутсорс" value={typeStats?.outsource || 0}
              prefix={<UserOutlined style={{ color: 'orange' }} />}
              valueStyle={{ color: empType === 'outsource' ? 'orange' : undefined }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small" hoverable style={{ cursor: 'pointer', borderColor: empType === 'service_account' ? 'purple' : undefined }}
            onClick={() => handleStatClick('service_account')}>
            <Statistic title="Сервисные" value={typeStats?.service_account || 0}
              prefix={<CloudServerOutlined style={{ color: 'purple' }} />}
              valueStyle={{ color: empType === 'service_account' ? 'purple' : undefined }} />
          </Card>
        </Col>
      </Row>

      <TableToolbar>
        <Space wrap>
          <Input placeholder="Поиск по ФИО или email" prefix={<SearchOutlined />}
            style={{ width: 280 }} value={search} allowClear
            onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, current: 1 })); }} />
          <Select style={{ width: 160 }} value={isActive}
            onChange={(v) => { setIsActive(v); setPagination(p => ({ ...p, current: 1 })); }}
            options={[
              { label: 'Активные',  value: true      },
              { label: 'Уволенные', value: false     },
              { label: 'Все',       value: undefined },
            ]} />
          <Select style={{ width: 180 }} value={empType} placeholder="Тип" allowClear
            onChange={(v) => { setEmpType(v); setPagination(p => ({ ...p, current: 1 })); }}
            options={[
              { label: 'Сотрудники',         value: 'employee' },
              { label: 'Аутсорс',            value: 'outsource' },
              { label: 'Сервисные аккаунты', value: 'service_account' },
            ]} />
          <Select placeholder="Компания" style={{ width: 180 }} value={company} allowClear
            onChange={(v) => { setCompany(v); setPagination(p => ({ ...p, current: 1 })); }}
            options={COMPANIES.map(c => ({ label: c, value: c }))} />
          <Select placeholder="Google доступ" style={{ width: 180 }} value={hasAccess} allowClear
            onChange={(v) => { setHasAccess(v); setPagination(p => ({ ...p, current: 1 })); }}
            options={[
              { label: 'С доступом Google', value: true  },
              { label: 'Без доступа',       value: false },
            ]} />
          {hasFilters && <Button onClick={resetFilters}>Сбросить</Button>}
        </Space>
      </TableToolbar>

      <Card size="small">
        <Table dataSource={items} columns={columns} rowKey="id" loading={isLoading} size="small"
          scroll={{ x: 1250 }}
          pagination={makeTablePagination({
            ...pagination,
            total,
            showTotal: (t) => `Всего ${t} сотрудников`,
          })}
          onChange={(pag) => setPagination(normalizeTablePagination(pag))}
          rowClassName={(r) => !r.is_active ? 'row-decommissioned' : ''}
          onRow={(record) => ({ onClick: () => setSelectedEmployee(record), style: { cursor: 'pointer' } })}
        />
      </Card>

      {/* Drawer */}
      <Drawer open={!!selectedEmployee} onClose={() => setSelectedEmployee(null)} width={600}
        title={selectedEmployee ? (
          <Space>
            <Text strong>{selectedEmployee.full_name}</Text>
            {selectedEmployee.is_active ? <Tag color="green">Активен</Tag> : <Tag color="red">Уволен</Tag>}
            {selectedEmployee.employee_type && selectedEmployee.employee_type !== 'employee' && (
              <Tag color={EMPLOYEE_TYPE_COLORS[selectedEmployee.employee_type]}>
                {EMPLOYEE_TYPE_LABELS[selectedEmployee.employee_type]}
              </Tag>
            )}
          </Space>
        ) : ''}
        extra={
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={openEdit}>Редактировать</Button>
            {equipment.length > 0 && (
              <>
                <Button size="small" danger icon={<RollbackOutlined />} onClick={() => setShowReturnModal(true)}>Снять всё</Button>
                <Button type="primary" size="small" icon={<FileTextOutlined />} onClick={() => setShowActModal(true)}>Акт</Button>
              </>
            )}
          </Space>
        }
      >
        {detailLoading && <div style={{ textAlign: 'center', padding: 40 }}>Загрузка...</div>}
        {employeeDetail && (
          <Tabs defaultActiveKey="info" items={[
            {
              key: 'info', label: 'Информация',
              children: (
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="Email">{employeeDetail.email || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Должность">{employeeDetail.job_title || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Город">{employeeDetail.location || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Тип">
                    <Tag color={EMPLOYEE_TYPE_COLORS[employeeDetail.employee_type] || 'default'}>
                      {EMPLOYEE_TYPE_LABELS[employeeDetail.employee_type] || '—'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Статус">
                    {employeeDetail.is_active
                      ? <Tag color="green">Активен</Tag>
                      : <><Tag color="red">Уволен</Tag>{employeeDetail.termination_date && <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>с {formatDate(employeeDetail.termination_date)}</Text>}</>
                    }
                  </Descriptions.Item>
                  <Descriptions.Item label="Дата выхода">{formatDate(employeeDetail.joining_date)}</Descriptions.Item>
                  <Descriptions.Item label="Юр. лица">
                    <Space direction="vertical" size={2}>
                      {(employeeDetail.companies || []).map(c => (
                        <div key={c.id}>
                          <Tag color={c.is_active ? 'blue' : 'default'}>{c.company_name}</Tag>
                          {c.start_date && <Text type="secondary" style={{ fontSize: 11 }}> c {formatDate(c.start_date)}{c.termination_date && ` по ${formatDate(c.termination_date)}`}</Text>}
                        </div>
                      ))}
                    </Space>
                  </Descriptions.Item>
                  {employeeDetail.tribune_id && <Descriptions.Item label="Tribune ID"><Text code style={{ fontSize: 11 }}>{employeeDetail.tribune_id}</Text></Descriptions.Item>}
                  {employeeDetail.last_synced_at && <Descriptions.Item label="Последний синк">{new Date(employeeDetail.last_synced_at).toLocaleString('ru-RU')}</Descriptions.Item>}
                  {employeeDetail.notes && <Descriptions.Item label="Примечания"><Text type="secondary">{employeeDetail.notes}</Text></Descriptions.Item>}
                </Descriptions>
              ),
            },
            {
              key: 'equipment', label: `Оборудование (${equipment.length})`,
              children: equipment.length === 0
                ? <Empty description="Оборудования нет" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                : <Table dataSource={equipment} rowKey="id" size="small" pagination={false}
                    columns={[
                      { title: 'Инв. №', dataIndex: 'inventory_number', width: 100, render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
                      { title: 'Тип', dataIndex: 'type', width: 110, ellipsis: true },
                      { title: 'Наименование', dataIndex: 'name', ellipsis: true, render: (v) => <Tooltip title={v}>{v}</Tooltip> },
                      { title: 'Место', dataIndex: 'location', width: 110, render: (v) => v || '—' },
                    ]}
                  />,
            },
            {
              key: 'acts',
              label: `Акты (${allActs.length})`,
              children: allActs.length === 0
                ? <Empty description="Актов нет" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                : <Table dataSource={allActs} rowKey={(r) => `${r._kind}-${r.id}`}
                    columns={actsColumns} size="small" pagination={false} />,
            },
            {
              key: 'licenses',
              label: (
                <Space size={4}>
                  🔑 Лицензии
                  {licensesCount > 0 && <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>{licensesCount}</Tag>}
                </Space>
              ),
              children: <LicensesTab employeeId={selectedEmployee?.id} />,
            },
            {
              key: 'access',
              label: (
                <Space size={4}>
                  <KeyOutlined />
                  Доступ
                  {employeeDetail.can_login && <Tag color="success" style={{ margin: 0, fontSize: 10 }}>●</Tag>}
                </Space>
              ),
              children: (
                <AccessTab employee={employeeDetail} currentUser={currentUser} />
              ),
            },
          ]} />
        )}
      </Drawer>

      {/* Редактирование */}
      <Modal open={showEditModal} title={`Редактировать: ${employeeDetail?.full_name}`}
        onOk={() => editForm.validateFields().then(v => updateMutation.mutate(v))}
        onCancel={() => { setShowEditModal(false); editForm.resetFields(); }}
        confirmLoading={updateMutation.isPending} okText="Сохранить" cancelText="Отмена" width={520}>
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="full_name" label="ФИО" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="email" label="Email"><Input /></Form.Item>
          <Form.Item name="job_title" label="Должность"><Input /></Form.Item>
          <Form.Item name="location" label="Город"><Input /></Form.Item>
          <Form.Item name="employee_type" label="Тип">
            <Select options={[
              { label: 'Сотрудник',         value: 'employee' },
              { label: 'Аутсорс',           value: 'outsource' },
              { label: 'Сервисный аккаунт', value: 'service_account' },
            ]} />
          </Form.Item>
          <Form.Item name="is_active" label="Статус" valuePropName="checked">
            <Switch checkedChildren="Активен" unCheckedChildren="Уволен" />
          </Form.Item>
          <Form.Item name="termination_date" label="Дата увольнения (ГГГГ-ММ-ДД)">
            <Input placeholder="2024-12-31" />
          </Form.Item>
          <Form.Item name="joining_date" label="Дата выхода (ГГГГ-ММ-ДД)"><Input placeholder="2024-01-15" /></Form.Item>
          <Form.Item name="notes" label="Примечания"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>

      {/* Снятие оборудования */}
      <Modal open={showReturnModal}
        title={<Space><WarningOutlined style={{ color: 'orange' }} /><span>Снять всё оборудование</span></Space>}
        onOk={() => {
          if (!returnLocation) { message.warning('Выберите место'); return; }
          returnAllMutation.mutate({ equipmentIds: equipment.map(e => e.id), locationId: returnLocation });
        }}
        onCancel={() => { setShowReturnModal(false); setReturnLocation(null); }}
        confirmLoading={returnAllMutation.isPending} okText="Снять всё" cancelText="Отмена"
        okButtonProps={{ danger: true }} width={560}>
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={`Будет снято ${equipment.length} позиций.`} />
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>Куда вернуть:</div>
          <Select showSearch placeholder="Выберите место" style={{ width: '100%' }}
            value={returnLocation} onChange={setReturnLocation} options={locationOptions}
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())} />
        </div>
        <Table dataSource={equipment} rowKey="id" size="small" pagination={false} scroll={{ y: 200 }}
          columns={[
            { title: 'Инв. №', dataIndex: 'inventory_number', width: 100, render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
            { title: 'Тип', dataIndex: 'type', width: 110, ellipsis: true },
            { title: 'Наименование', dataIndex: 'name', ellipsis: true, render: (v) => <Tooltip title={v}>{v}</Tooltip> },
          ]}
        />
      </Modal>

      {/* Предложение акта возврата */}
      <Modal open={showPostReturnModal} title="Сформировать акт возврата?"
        onCancel={() => setShowPostReturnModal(false)}
        footer={[
          <Button key="skip" onClick={() => setShowPostReturnModal(false)}>Пропустить</Button>,
          <Button key="act" type="primary" icon={<RollbackOutlined />}
            onClick={() => { setShowPostReturnModal(false); setShowReturnActModal(true); }}>
            Сформировать акт
          </Button>,
        ]}
      >
        <p>С сотрудника <b>{returnActData?.employee?.full_name}</b> снято <b>{returnActData?.equipment?.length}</b> позиций.</p>
        <p>Сформировать акт возврата техники?</p>
      </Modal>

      {returnActData && (
        <ReturnActModal
          open={showReturnActModal}
          onClose={() => {
            setShowReturnActModal(false);
            setReturnActData(null);
            queryClient.invalidateQueries(['employee-return-acts', selectedEmployee?.id]);
          }}
          employee={returnActData.employee}
          equipment={returnActData.equipment}
        />
      )}

      <ActModal
        open={showActModal}
        onClose={() => {
          setShowActModal(false);
          queryClient.invalidateQueries(['employee-acts', selectedEmployee?.id]);
        }}
        employee={employeeDetail}
        equipment={equipment}
      />

      {/* Добавление */}
      <Modal open={showAddModal} title="Добавить сотрудника"
        onOk={() => addForm.validateFields().then(v => createMutation.mutate({ ...v, company_ids: v.company_ids || [], is_active: true }))}
        onCancel={() => { setShowAddModal(false); addForm.resetFields(); }}
        confirmLoading={createMutation.isPending} okText="Добавить" cancelText="Отмена" width={520}>
        <Form form={addForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="full_name" label="ФИО" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="email" label="Email"><Input /></Form.Item>
          <Form.Item name="job_title" label="Должность"><Input /></Form.Item>
          <Form.Item name="location" label="Город"><Input /></Form.Item>
          <Form.Item name="joining_date" label="Дата выхода (ГГГГ-ММ-ДД)"><Input placeholder="2024-01-15" /></Form.Item>
          <Form.Item name="employee_type" label="Тип" initialValue="employee">
            <Select options={[
              { label: 'Сотрудник',         value: 'employee' },
              { label: 'Аутсорс',           value: 'outsource' },
              { label: 'Сервисный аккаунт', value: 'service_account' },
            ]} />
          </Form.Item>
          <Form.Item name="company_ids" label="Юр. лица">
            <Select mode="multiple" placeholder="Выберите юр. лицо"
              options={(companiesData || []).map(c => ({ label: c.name, value: c.id }))} />
          </Form.Item>
          <Form.Item name="notes" label="Примечания"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}